import { isRecentLoggedIn, loginAs } from 'resources/auth'
import { useDeleteOrganization } from 'lib/superAdminHooks'
import { h, Fragment } from 'preact'
import { memo, useState } from 'preact/compat'
import PropTypes from 'prop-types'
import PNFO from 'jlinc-shared/PNFO'
import TextInput from 'components/TextInput'

import usePrevious from 'lib/usePreviousHook'
import classNames from 'lib/classNames'
import { useLocation } from 'lib/locationResource'
import ErrorMessage from 'components/ErrorMessage'
import Loading from 'components/Loading'
import Header from 'components/Header'
import OrganizationIcon from 'components/OrganizationIcon'
import IconRow from 'components/IconRow'
import IconRowList from 'components/IconRowList'
import Link from 'components/Link'
import OrganizationNameAndTagLine from 'components/OrganizationNameAndTagLine'
import OrganizationMembershipPills from 'components/OrganizationMembershipPills'
import { hrefToOrganization } from 'components/LinkToOrganization'
import Form from 'components/Form'
import Button from 'components/Button'
import Modal from 'components/Modal'

import './index.sass'
import Icon from 'components/Icon'

function SimpleLoginForm({ onSuccess }) {
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    if (!login) {
      setError('Email or mobile phone is required')
      setLoading(false)
      return
    }

    try {
      await loginAs({
        login,
        password,
        keepMeLoggedIn: true
      })
      onSuccess()
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Row>
        <Form.Item>
          <Form.Label>EMAIL OR MOBILE PHONE</Form.Label>
          <TextInput
            type="text"
            value={login}
            onInput={setLogin}
            required
            disabled={loading}
            autoComplete="username"
          />
        </Form.Item>
      </Form.Row>
      <Form.Row>
        <Form.Item>
          <Form.Label>PASSWORD</Form.Label>
          <TextInput
            type="password"
            value={password}
            onInput={setPassword}
            required
            disabled={loading}
            autoComplete="current-password"
          />
        </Form.Item>
      </Form.Row>
      {error && (
        <Form.Row>
          <p style={{ color: 'red' }}>{error}</p>
        </Form.Row>
      )}
      <Form.ButtonRow>
        <Button
          type="primary"
          submit
          disabled={loading || !password || !login}
        >
          {loading ? 'Logging in...' : 'Login'}
        </Button>
      </Form.ButtonRow>
    </Form>
  )
}

export default function OrganizationList({
  className,
  error,
  organizations,
  filter,
  textFilter,
  map,
  sortBy,
  activeOrganizationApikey,
  onEmpty,
  onEmptyAfterTextFilter = `No ${PNFO.plural} match "${textFilter}"`,
  showMembershipIcon,
  showMembershipPills,
  animateChanges,
  organizationsLoading,
  ...props
}) {
  if (error) return <ErrorMessage {...{ className, error }} />
  if (!organizations || organizationsLoading) return <Loading {...{ className, type: 'block' }} />

  organizations = organizations.map(organization => ({
    ...organization,
    active: organization.apikey === activeOrganizationApikey,
  }))

  if (filter) organizations = organizations.filter(filter)

  const empty = organizations.length === 0

  if (textFilter) textFilter = textFilter.toLowerCase().trim()
  if (textFilter) organizations = organizations.filter(organization =>
    `${organization.name} ${organization.tag_line}`.toLowerCase().includes(textFilter)
  )

  const emptyAfterTextFilter = organizations.length === 0

  if (map) organizations = organizations.map(map)
  if (sortBy) organizations.sort(sortBy)

  return <IconRowList
    className={classNames('OrganizationList', { animateChanges, className })}
    {...props}
  >
    {
      empty ? (onEmpty ? <Empty>{onEmpty}</Empty> : null) :
      emptyAfterTextFilter ? <Empty>{onEmptyAfterTextFilter}</Empty> :
      <Members {...{
        organizations,
        showMembershipIcon,
        showMembershipPills,
      }} />
    }
  </IconRowList>
}

OrganizationList.propTypes = {
  className: PropTypes.string,
  error: ErrorMessage.propTypes.error,
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      apikey: PropTypes.string.isRequired,
      consumer_icon: PropTypes.string,
      name: PropTypes.string.isRequired,
      tag_line: PropTypes.string,
      buttons: PropTypes.node,
      subscription: PropTypes.object,
      membership: PropTypes.object,
      haveSISA: PropTypes.bool,
      hasPendingOrganizationMemberships: PropTypes.bool,
    })
  ),
  filter: PropTypes.func,
  textFilter: PropTypes.string,
  map: PropTypes.func,
  sortBy: PropTypes.func,
  activeOrganizationApikey: PropTypes.string,
  onEmpty: PropTypes.node,
  onEmptyAfterTextFilter: PropTypes.node,
  showMembershipIcon: PropTypes.bool,
  showMembershipPills: PropTypes.bool,
  animateChanges: PropTypes.bool,
  organizationsLoading: PropTypes.bool,
}

function Empty({ children }) {
  return <Header size="sm" centered italic className="OrganizationList-Empty">
    {children}
  </Header>
}

function Members({ organizations, ...options }) {
  const apikeys = new Set()
  const members = organizations
    .filter(({ apikey }) => {
      if (apikeys.has(apikey)) return false
      apikeys.add(apikey); return true
    })
    .map((props, index) =>
      <div key={props.apikey} style={{ '--index': index }}>
        <OrganizationList.Member {...{ ...options, ...props }} />
      </div>
    )

  const previousMembers = usePrevious(members)
  if (previousMembers) {
    // we have to keep the new members in the same sort order as the previous members
    // to ensure Preact doesnt create new dom nodes so the css animation works
    const keyOrder = previousMembers.map(x => x.key)
    members.sort((a, b) => keyOrder.indexOf(a.key) - keyOrder.indexOf(b.key))
  }

  if (members.length > 0)
    members[members.length - 1].props.className = "OrganizationList-Member-last"

  return <div
    className="OrganizationList-Members"
    style={{ '--length': members.length }}
  >
    {members}
  </div>
}

OrganizationList.Member = memo(function({
  className,
  showMembershipPills,
  apikey,
  consumer_icon,
  name,
  tag_line,
  active,
  selected,
  membership,
  hasPendingOrganizationMemberships,
  href = hrefToOrganization(apikey),
  buttons,
}) {
  const location = useLocation()
  const [showDeleteFlow, setShowDeleteFlow] = useState(false)
  const [localError, setLocalError] = useState(null)
  const {
    deleteOrganization,
    deletingOrganizationError
  } = useDeleteOrganization(apikey, 'OrganizationList.Member')

  function openDeleteFlow() {
    setShowDeleteFlow(true)
  }

  function closeDeleteFlow() {
    setShowDeleteFlow(false)
    setLocalError(null)
  }

  function handleConfirmDelete() {
    deleteOrganization().catch(err => {
      setLocalError(err.message || 'Failed to delete organization')
    })
    setShowDeleteFlow(false)
  }

  return <IconRow {...{
    className: classNames('OrganizationList-Member', {
      className, active, selected,
    }),
    href,
    icon: <OrganizationIcon
      size="sm"
      organization={{ apikey, name, consumer_icon }}
      brandingType="consumer"
    />,
    row: <OrganizationNameAndTagLine
      classNamePrefix="OrganizationList-Member"
      organizationName={name}
      organizationTagLine={tag_line}
    />,
    buttons: <Fragment>
      {hasPendingOrganizationMemberships &&
        <Link href={`/${apikey}/members`} tabIndex={-1}>❗</Link>
      }
      {location.pathname === '/admin/dashboard' ? <p className="admin_btn">super admin</p> :
      <p>  {showMembershipPills && membership &&
          <OrganizationMembershipPills {...{ organizationMembership: membership }} />}
      </p>}
      {buttons}

      {location.pathname === '/admin/dashboard' && <Icon type="delete" onclick={openDeleteFlow} />}

      {showDeleteFlow && (
        <Modal onClose={closeDeleteFlow}>
          {!isRecentLoggedIn() ? (
            <div className="OrganizationList-LoginModal">
              <Header size="lg">Confirm Your Identity</Header>
              <p className="OrganizationList-LoginModal-description">
                Please log in to continue with deleting this organization.
              </p>
              <SimpleLoginForm onSuccess={() => {
                setShowDeleteFlow(false)
                setTimeout(() => setShowDeleteFlow(true), 100)
              }} />
              <div className="OrganizationList-LoginModal-footer">
                <Button onClick={closeDeleteFlow}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="OrganizationList-DeleteModal">
              <Header size="lg">Delete Organization</Header>
              <p>Are you sure you want to delete <strong>{name}</strong>?</p>
              <p className="OrganizationList-DeleteModal-warning">This action cannot be undone.</p>
              <Form.ButtonRow>
                <Button onClick={closeDeleteFlow}>Cancel</Button>
                <Button
                  type="normal"
                  style={{ marginLeft: '8px', borderColor: 'red', color: 'red' }}
                  onClick={handleConfirmDelete}>
                Delete Organization
                </Button>
              </Form.ButtonRow>
            </div>
          )}
        </Modal>
      )}

      {(localError || deletingOrganizationError) && (
        <Modal onClose={() => setLocalError(null)}>
          <div className="OrganizationList-ErrorModal">
            <Header size="lg">Error</Header>
            <p className="OrganizationList-ErrorModal-message">
              {localError || deletingOrganizationError?.message || 'An error occurred'}
            </p>
            <Form.ButtonRow>
              <Button
                type="primary"
                onClick={
                  () => {
                    setLocalError(null)
                    closeDeleteFlow()
                  }
                }
              >
                Close
              </Button>
            </Form.ButtonRow>
          </div>
        </Modal>
      )}

    </Fragment>,
  }} />
})
